import React, { useEffect, useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { useManagerPortalConfig } from '../manager/useManagerPortalConfig';

const WIDTH_OPTIONS = [
  { value: 'normal', label: 'Normal', description: 'Standard-Bestellscheinbreite für kompakte POS-Terminals.' },
  { value: 'wide', label: 'Breit', description: 'Mehr Platz für Artikel- und Modifier-Text.' },
  { value: 'xwide', label: 'Extra breit', description: 'Maximaler Platz im Bestellschein rechts.' },
];

export default function AdminArticleScreens({ embedded = false }) {
  const { portalConfig, isLoading, savePortalConfig } = useManagerPortalConfig();
  const [selectedWidth, setSelectedWidth] = useState('normal');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!portalConfig) return;
    setSelectedWidth(portalConfig?.posTerminal?.orderPanelWidth || 'normal');
  }, [portalConfig]);

  const save = async () => {
    setSaving(true);
    await savePortalConfig(
      (prev) => ({
        ...prev,
        posTerminal: {
          ...prev?.posTerminal,
          orderPanelWidth: selectedWidth,
        },
      }),
      'Bestellschein-Einstellungen gespeichert',
    );
    setSaving(false);
  };

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center ${embedded ? 'min-h-[220px]' : 'min-h-[60vh]'}`}>
        <div className="rounded-3xl border border-slate-200 bg-white px-6 py-5 text-sm text-slate-500 shadow-sm">
          Lade POS-Konfiguration…
        </div>
      </div>
    );
  }

  return (
    <div className={`${embedded ? 'space-y-6' : 'space-y-6 p-6'}`}>
      <div className="space-y-3">
        <p className="text-sm font-semibold uppercase tracking-[0.22em] text-slate-400">Artikel</p>
        <h1 className="text-3xl font-bold text-[#132d39]">Bestellbildschirme</h1>
        <p className="max-w-3xl text-slate-600">
          Hier konfigurieren Sie die Erscheinung des POS-Bestellscheins und wie viel Platz die rechte Bestellübersicht erhält.
          Die gewählte Breite wird im POS-Terminal übernommen.
        </p>
      </div>

      <Card className="rounded-[28px] border-slate-200 bg-white shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-xl font-bold tracking-tight text-[#314b59]">Bestellschein-Größe</CardTitle>
          <p className="text-sm text-slate-500">Wählen Sie die Breite des Bestellscheins im POS-Terminal.</p>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-3">
            <Select value={selectedWidth} onValueChange={setSelectedWidth}>
              <SelectTrigger className="w-72">
                <SelectValue placeholder="Bestellschein-Breite wählen" />
              </SelectTrigger>
              <SelectContent>
                {WIDTH_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="space-y-2 text-sm text-slate-600">
              {WIDTH_OPTIONS.filter((option) => option.value === selectedWidth).map((option) => (
                <p key={option.value}>{option.description}</p>
              ))}
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-semibold text-slate-700">Vorschau</p>
            <div className="mt-4 grid gap-4 md:grid-cols-3">
              {WIDTH_OPTIONS.map((option) => (
                <div
                  key={option.value}
                  className={`rounded-2xl border p-4 text-sm ${option.value === selectedWidth ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-white'}`}>
                  <p className="font-semibold text-slate-900">{option.label}</p>
                  <p className="mt-2 text-slate-500">{option.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button onClick={save} className="rounded-2xl bg-[#71c545] text-white hover:bg-[#63b13b]" disabled={saving}>
              {saving ? 'Speichert…' : 'Speichern'}
            </Button>
            <Button variant="outline" onClick={() => setSelectedWidth(portalConfig?.posTerminal?.orderPanelWidth || 'normal')}>
              Zurücksetzen
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
